import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { CustomToggle } from '../components/CustomToggle';
import { TermsAndConditionsContent } from '../components/TermsAndConditionsContent';
import { SignupProgress, USER_SIGNUP_STEPS } from '../components/SignupProgress';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';

export const RegisterVerifyPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;

    const token = (searchParams.get('token') || '').trim();
    const [status, setStatus] = useState<'loading' | 'terms' | 'accepting' | 'err'>('loading');
    const [message, setMessage] = useState<string | null>(null);
    const [acceptTerms, setAcceptTerms] = useState(false);

    useEffect(() => {
        document.title = 'Verify email | Calculist';
    }, []);

    useEffect(() => {
        if (!selfServeSignup || !token || flagsLoading) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await authApiService.signupRegisterCheckStatus(token);
                if (cancelled) return;
                if (data.step === 'done') {
                    navigate('/auth/login', { replace: true });
                } else if (data.step === 'password') {
                    navigate(`/auth/register/password?token=${encodeURIComponent(token)}`, { replace: true });
                } else {
                    setStatus('terms');
                }
            } catch (err: unknown) {
                debugError('signupRegisterCheckStatus', err);
                if (!cancelled) {
                    setStatus('err');
                    setMessage(err instanceof Error ? err.message : 'Verification failed.');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [selfServeSignup, token, flagsLoading, navigate]);

    const handleAccept = async () => {
        if (!acceptTerms) return;
        setStatus('accepting');
        try {
            await authApiService.signupRegisterAcceptTerms(token);
            navigate(`/auth/register/password?token=${encodeURIComponent(token)}`);
        } catch (err: unknown) {
            debugError('signupRegisterAcceptTerms', err);
            setStatus('err');
            setMessage(err instanceof Error ? err.message : 'Could not record acceptance. Please try again.');
        }
    };

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;

    const wrapper = (content: React.ReactNode) => (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            {content}
        </div>
    );

    if (!token) {
        return <Navigate to="/auth/login" replace />;
    }

    if (status === 'loading') {
        return wrapper(<p style={themedStyles.text.muted}>Verifying your email…</p>);
    }

    if (status === 'err') {
        return wrapper(
            <div style={themedStyles.card.default}>
                <p style={themedStyles.text.body}>{message}</p>
                <Link to="/auth/login" style={{ color: theme.colors.primary }}>Try again</Link>
            </div>
        );
    }

    return wrapper(
        <div style={{ ...themedStyles.card.default, maxWidth: '560px' }}>
            <SignupProgress currentStep={1} steps={USER_SIGNUP_STEPS} />

            <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                <img src="/logo-name.svg" alt="Calculist" style={{ height: '64px', width: 'auto', marginBottom: theme.spacing.md }} />
                <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Review &amp; Accept Terms</h1>
                <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Your email is confirmed. Please review and accept the terms to continue.</p>
            </div>

            <div style={{
                maxHeight: '320px',
                overflowY: 'auto',
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.lg,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
                lineHeight: 1.6,
            }}>
                <TermsAndConditionsContent />
            </div>

            <div style={{ marginBottom: theme.spacing.lg }}>
                <CustomToggle
                    checked={acceptTerms}
                    onChange={setAcceptTerms}
                    label="I have read and accept the Terms and Conditions"
                    disabled={status === 'accepting'}
                />
            </div>

            <button
                type="button"
                onClick={() => void handleAccept()}
                disabled={!acceptTerms || status === 'accepting'}
                style={{
                    ...themedStyles.button.primary,
                    ...(!acceptTerms || status === 'accepting' ? themedStyles.button.disabled : {}),
                    width: '100%',
                    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                }}
            >
                {status === 'accepting' ? 'Saving…' : 'Accept Terms'}
            </button>
        </div>
    );
};
